import React, { useState, useEffect } from 'react';
import { Calendar, Clock, Users, Phone, Mail, Check, X, Trash2, LogOut, Filter, RefreshCw } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminDashboard() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [storedPassword, setStoredPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [reservations, setReservations] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, confirmed: 0, cancelled: 0 });
  const [loading, setLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterDate, setFilterDate] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    try {
      const response = await axios.post(`${API}/admin/login`, { password });
      if (response.data.success) {
        setIsAuthenticated(true);
        setStoredPassword(password);
        fetchReservations(password);
      }
    } catch (err) {
      setLoginError('Invalid password');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setStoredPassword('');
    setPassword('');
    setReservations([]);
  };

  const fetchReservations = async (pwd = storedPassword) => {
    setLoading(true);
    try {
      let url = `${API}/admin/reservations?password=${pwd}`;
      if (filterStatus) url += `&status=${filterStatus}`;
      if (filterDate) url += `&date=${filterDate}`;
      
      const response = await axios.get(url);
      setReservations(response.data.reservations);
      setStats(response.data.stats);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Error fetching reservations:', err);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/reservations/${id}?password=${storedPassword}`, { status });
      fetchReservations();
    } catch (err) {
      console.error('Error updating reservation:', err);
    }
  };

  const deleteReservation = async (id) => {
    if (!window.confirm('Are you sure you want to delete this reservation?')) return;
    try {
      await axios.delete(`${API}/admin/reservations/${id}?password=${storedPassword}`);
      fetchReservations();
    } catch (err) {
      console.error('Error deleting reservation:', err);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchReservations();
    }
  }, [filterStatus, filterDate, isAuthenticated]);

  // Auto-refresh every 10 seconds when authenticated
  useEffect(() => {
    let interval;
    if (isAuthenticated) {
      interval = setInterval(() => {
        fetchReservations();
      }, 10000); // Refresh every 10 seconds
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isAuthenticated, storedPassword, filterStatus, filterDate]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-yellow-100 text-yellow-800';
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#FBF9F6] flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full border border-[#E6D8C8]">
          <div className="text-center mb-8">
            <h1 className="font-['Cormorant_Garamond'] text-3xl font-medium text-[#2A1E17] mb-2">
              Rui's Restaurante
            </h1>
            <p className="text-[#6B584B] text-sm">Admin Dashboard</p>
          </div>
          
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#2A1E17] mb-2">
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border border-[#E6D8C8] rounded-none bg-[#FBF9F6] focus:ring-1 focus:ring-[#8C5A2B] focus:border-[#8C5A2B] outline-none"
                placeholder="Enter admin password"
                data-testid="admin-password-input"
              />
            </div>
            
            {loginError && (
              <p className="text-red-600 text-sm">{loginError}</p>
            )}
            
            <button
              type="submit"
              data-testid="admin-login-btn"
              className="w-full bg-[#2A1E17] text-[#FBF9F6] py-3 uppercase tracking-wider text-sm font-medium hover:bg-[#8C5A2B] transition-colors"
            >
              Login
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FBF9F6]">
      {/* Header */}
      <header className="bg-[#2A1E17] text-[#FBF9F6] py-4 px-6">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div>
            <h1 className="font-['Cormorant_Garamond'] text-2xl font-medium">Rui's Restaurante</h1>
            <p className="text-[#8C5A2B] text-sm">Admin Dashboard</p>
          </div>
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2 text-sm text-[#E6D8C8]">
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
              <span>Auto-refresh ON</span>
              {lastUpdated && (
                <span className="text-[#8C5A2B]">
                  • Updated {lastUpdated.toLocaleTimeString()}
                </span>
              )}
            </div>
            <button
              onClick={handleLogout}
              data-testid="admin-logout-btn"
              className="flex items-center gap-2 text-sm hover:text-[#8C5A2B] transition-colors"
            >
              <LogOut size={18} />
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white p-6 border border-[#E6D8C8]">
            <p className="text-[#6B584B] text-sm mb-1">Total</p>
            <p className="text-3xl font-['Cormorant_Garamond'] text-[#2A1E17]">{stats.total}</p>
          </div>
          <div className="bg-yellow-50 p-6 border border-yellow-200">
            <p className="text-yellow-700 text-sm mb-1">Pending</p>
            <p className="text-3xl font-['Cormorant_Garamond'] text-yellow-800">{stats.pending}</p>
          </div>
          <div className="bg-green-50 p-6 border border-green-200">
            <p className="text-green-700 text-sm mb-1">Confirmed</p>
            <p className="text-3xl font-['Cormorant_Garamond'] text-green-800">{stats.confirmed}</p>
          </div>
          <div className="bg-red-50 p-6 border border-red-200">
            <p className="text-red-700 text-sm mb-1">Cancelled</p>
            <p className="text-3xl font-['Cormorant_Garamond'] text-red-800">{stats.cancelled}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white p-4 border border-[#E6D8C8] mb-6 flex flex-wrap gap-4 items-center">
          <Filter size={18} className="text-[#6B584B]" />
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            data-testid="filter-status"
            className="px-4 py-2 border border-[#E6D8C8] bg-[#FBF9F6] text-[#2A1E17] text-sm"
          >
            <option value="">All Status</option>
            <option value="pending">Pending</option>
            <option value="confirmed">Confirmed</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <input
            type="date"
            value={filterDate}
            onChange={(e) => setFilterDate(e.target.value)}
            data-testid="filter-date"
            className="px-4 py-2 border border-[#E6D8C8] bg-[#FBF9F6] text-[#2A1E17] text-sm"
          />
          {(filterStatus || filterDate) && (
            <button
              onClick={() => { setFilterStatus(''); setFilterDate(''); }}
              className="text-sm text-[#8C5A2B] hover:underline"
            >
              Clear filters
            </button>
          )}
        </div>

        {/* Reservations Table */}
        <div className="bg-white border border-[#E6D8C8] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="reservations-table">
              <thead className="bg-[#F0EAE1]">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-[#6B584B] uppercase tracking-wider">Date & Time</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-[#6B584B] uppercase tracking-wider">Guest</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-[#6B584B] uppercase tracking-wider">Contact</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-[#6B584B] uppercase tracking-wider">Party</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-[#6B584B] uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-[#6B584B] uppercase tracking-wider">Special Requests</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-[#6B584B] uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E6D8C8]">
                {loading ? (
                  <tr>
                    <td colSpan="7" className="px-4 py-8 text-center text-[#6B584B]">Loading...</td>
                  </tr>
                ) : reservations.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-4 py-8 text-center text-[#6B584B]">No reservations found</td>
                  </tr>
                ) : (
                  reservations.map((res) => (
                    <tr key={res.id} className="hover:bg-[#FBF9F6]" data-testid={`reservation-row-${res.id}`}>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2">
                          <Calendar size={14} className="text-[#8C5A2B]" />
                          <span className="text-[#2A1E17] font-medium">{res.date}</span>
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <Clock size={14} className="text-[#8C5A2B]" />
                          <span className="text-[#6B584B] text-sm">{res.time}</span>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <p className="text-[#2A1E17] font-medium">{res.name}</p>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2 text-sm text-[#6B584B]">
                          <Mail size={14} />
                          {res.email}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-[#6B584B] mt-1">
                          <Phone size={14} />
                          {res.phone}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2">
                          <Users size={14} className="text-[#8C5A2B]" />
                          <span className="text-[#2A1E17]">{res.guests}</span>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <span className={`px-2 py-1 text-xs font-medium rounded ${getStatusColor(res.status)}`}>
                          {res.status}
                        </span>
                      </td>
                      <td className="px-4 py-4 max-w-[200px]">
                        <p className="text-sm text-[#6B584B] truncate">{res.special_requests || '-'}</p>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2">
                          {res.status !== 'confirmed' && (
                            <button
                              onClick={() => updateStatus(res.id, 'confirmed')}
                              data-testid={`confirm-btn-${res.id}`}
                              className="p-2 text-green-600 hover:bg-green-50 rounded transition-colors"
                              title="Confirm"
                            >
                              <Check size={16} />
                            </button>
                          )}
                          {res.status !== 'cancelled' && (
                            <button
                              onClick={() => updateStatus(res.id, 'cancelled')}
                              data-testid={`cancel-btn-${res.id}`}
                              className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                              title="Cancel"
                            >
                              <X size={16} />
                            </button>
                          )}
                          <button
                            onClick={() => deleteReservation(res.id)}
                            data-testid={`delete-btn-${res.id}`}
                            className="p-2 text-gray-600 hover:bg-gray-100 rounded transition-colors"
                            title="Delete"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
